import { addPlanToGroups } from '@db/Groups';
import { addPhotosToNewPlan } from '@db/Photos';
import { formatPlanInput } from '@db/Plans/Common';
import { addPlanContactInvite, addPlanUserInvite } from '@db/Plans/Request';
import Constants from '@helpers/Constants';
import { logError } from '@helpers/Loggers';
import { sendSMSMessage } from '@helpers/SMS';
import { getCurrentUser } from '@helpers/firebase/Auth';
import db, { addDocWithTimestamps } from '@helpers/firebase/Firestore';
import { firestore } from '@helpers/firebase/Initialize';
import { Person, PersonType, PlanFormData } from '@types';
import { Place } from '@types';

export async function addPlan(
  inputPlan: PlanFormData,
  groupIds: string[],
  people: Person[]
): Promise<string> {
  const { location, photos, ...plan } = formatPlanInput(inputPlan);
  const planId = await addPlanDoc({
    ...plan,
    location,
    // `photos` not included as they are only
    // converted to download URLs after upload
    photos: [],
  });
  try {
    await Promise.all([
      addPhotosToNewPlan(planId, photos),
      addPeopleToPlan(planId, people, location),
      addPlanToGroups(groupIds, planId),
    ]);
  } catch (error) {
    // TODO: handle these errors differently, some options include:
    //   - delete plan and surface error
    //   - keep plan and surface error
    //   - dont surface error (current)
    logError(error);
  }
  return planId;
}

export async function addPlanDoc(plan: PlanFormData) {
  const currentUser = getCurrentUser();
  const { displayName } = currentUser;

  const data = {
    ...plan,
    userId: currentUser.uid,
    user: { displayName, photoUrl: currentUser.photoURL ?? '' },
  };
  const docRef = await addDocWithTimestamps(db.collection(firestore, 'plans'), data);
  return docRef.id;
}

export async function addPeopleToPlan(planId: string, people: Person[], location: Place) {
  const promises = [];
  const phoneNumbers = [];
  for (const person of people) {
    switch (person.type) {
      case PersonType.CONTACT:
        if (person.phoneNumber) {
          promises.push(addPlanContactInvite(planId, person.personId, person.phoneNumber));
          phoneNumbers.push(person.phoneNumber);
        }
        break;
      case PersonType.USER:
        promises.push(addPlanUserInvite(planId, person.personId));
        break;
      default:
        logError(new Error(`Unrecognized person type: ${person.type}`));
    }
  }
  if (phoneNumbers.length > 0) {
    await sendSMSMessage(
      `I want to invite you to my plan in ${location.displayName}! Check it out on Joinstar at: ${Constants.joinstar.company.links.landing}`,
      phoneNumbers
    );
  }
  await Promise.all(promises);
}
